import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Ionicons } from '@expo/vector-icons';
import { Href, Link } from 'expo-router';
import React from 'react';
import { View } from 'react-native';
import { AnswerBox } from './answer-box';
import { Crumbs } from './seo-blocks';

/** Selos padrão sob o botão. Nada de "Grátis" solto: o grátis tem limite. */
export const HERO_BADGES = ['Comece grátis', 'Cliente marca sem baixar app'];

interface SeoHeroProps {
  crumbs: { label: string; href?: Href }[];
  title: string;
  /** Uma linha. Texto longo não entra aqui. */
  sub?: string;
  /** Linha pequena acima do título (ex.: data do guia). */
  meta?: string;
  /** Mostra o CTA principal e os "vistos" de risco. */
  cta?: boolean;
  /** Selos sob o botão (padrão: `HERO_BADGES`). */
  badges?: string[];
  /** Bloco de resposta logo depois do H1 (1 ou 2 frases, sem link). */
  answer?: string;
  /** Visual abaixo do texto: palco de celulares, resposta rápida, busca... */
  children?: React.ReactNode;
  /** Largura do texto: `wide` para landing, `narrow` para leitura. */
  width?: 'wide' | 'narrow';
}

/** Hero único das páginas de SEO: centralizado, título grande, uma linha, um botão. */
export function SeoHero({
  crumbs,
  title,
  sub,
  meta,
  cta,
  badges = HERO_BADGES,
  answer,
  children,
  width = 'wide',
}: SeoHeroProps) {
  return (
    <View className="px-4 pt-8 md:px-8 md:pt-16">
      <View className={`mx-auto w-full items-center ${width === 'wide' ? 'max-w-5xl' : 'max-w-4xl'}`}>
        <Crumbs items={crumbs} center />
        {meta ? <ThemedText className="mt-6 text-sm text-neutral-500">{meta}</ThemedText> : null}
        <ThemedText
          role="heading"
          aria-level={1}
          className={`${meta ? 'mt-3' : 'mt-6'} max-w-4xl text-balance text-center font-bold tracking-tight text-neutral-900 ${
            width === 'wide' ? 'text-4xl md:text-6xl lg:text-7xl' : 'text-4xl md:text-5xl'
          }`}
        >
          {title}
        </ThemedText>
        {sub ? (
          <ThemedText className="mt-4 max-w-xl text-balance text-center text-lg text-neutral-500">
            {sub}
          </ThemedText>
        ) : null}
        {answer ? (
          <View className="mt-6 w-full max-w-3xl">
            <AnswerBox text={answer} />
          </View>
        ) : null}
        {cta ? (
          <>
            <Link href="/(auth)/register" asChild>
              <Button size="lg" title="Criar conta grátis" className="mt-8 w-full max-w-[320px] md:w-auto" />
            </Link>
            <View className="mt-4 flex-row flex-wrap justify-center gap-x-4 gap-y-1">
              {badges.map((label) => (
                <View key={label} className="flex-row items-center gap-1">
                  <Ionicons name="checkmark" size={16} color="#FD5001" />
                  <ThemedText className="text-sm text-neutral-500">{label}</ThemedText>
                </View>
              ))}
            </View>
          </>
        ) : null}
      </View>
      {children ? <View className="mx-auto mt-10 w-full max-w-6xl">{children}</View> : null}
    </View>
  );
}
